import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { validateDateBR } from '../../lib/helpers';
import {
  Target,
  Plus,
  Users,
  CheckCircle,
  Clock,
  Eye,
  Trash2,
  XCircle,
  Calendar,
  BarChart3,
  Search
} from 'lucide-react';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const Feedback360MainV2 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [concluidos, setConcluidos] = useState([]);
  const [modalDetalhesAberto, setModalDetalhesAberto] = useState(false);
  const [modalRespostasAberto, setModalRespostasAberto] = useState(false);
  const [cicloDetalhes, setCicloDetalhes] = useState(null);
  const [respostasCiclo, setRespostasCiclo] = useState(null);
  const [loadingRespostas, setLoadingRespostas] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    data_inicio: '',
    data_fim: '',
    funcionario_avaliado: '',
    avaliadores_gestor: [],
    avaliadores_pares: [],
    avaliadores_clientes: [],
    avaliadores_subordinados: []
  });
  const [filtroGestor, setFiltroGestor] = useState('');
  const [filtroPares, setFiltroPares] = useState('');
  const [filtroClientes, setFiltroClientes] = useState('');
  const [filtroSubordinados, setFiltroSubordinados] = useState('');

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  const carregarDadosIniciais = async () => {
    await Promise.all([
      carregarFuncionarios(),
      carregarPendentes(),
      carregarConcluidos()
    ]);
  };

  const carregarFuncionarios = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    }
  };

  const carregarPendentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback360/pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendentes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar pendentes:', error);
      setPendentes([]);
    }
  };

  const carregarConcluidos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback360/concluidos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConcluidos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar concluídos:', error);
      setConcluidos([]);
    }
  };

  const toggleAvaliador = (lista, funcionarioId) => {
    if (lista.includes(funcionarioId)) {
      return lista.filter(id => id !== funcionarioId);
    }
    return [...lista, funcionarioId];
  };

  const criarAvaliacaoCompleta = async () => {
    if (!formData.nome || !formData.data_inicio || !formData.data_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!validateDateBR(formData.data_inicio, { pastYears: 15, futureYears: 15 }) || !validateDateBR(formData.data_fim, { pastYears: 15, futureYears: 15 })) {
      toast.error('Data de início ou fim inválida');
      return;
    }
    if (formData.data_fim < formData.data_inicio) {
      toast.error('A data fim não pode ser menor que a data início');
      return;
    }

    if (!formData.funcionario_avaliado) {
      toast.error('Selecione o funcionário a ser avaliado');
      return;
    }

    const totalAvaliadores = formData.avaliadores_gestor.length +
                            formData.avaliadores_pares.length + 
                            formData.avaliadores_clientes.length + 
                            formData.avaliadores_subordinados.length;

    if (totalAvaliadores === 0) {
      toast.error('Selecione pelo menos um avaliador');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const cicloResponse = await axios.post(
        `${API}/api/feedback360/ciclos`,
        {
          nome: formData.nome,
          descricao: formData.descricao,
          data_inicio: formData.data_inicio,
          data_fim: formData.data_fim,
          criado_por: user.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const cicloId = cicloResponse.data.id;

      await axios.put(
        `${API}/api/feedback360/ciclos/${cicloId}/avaliadores`,
        {
          funcionario_id: formData.funcionario_avaliado,
          avaliadores_gestor: formData.avaliadores_gestor,
          avaliadores_pares: formData.avaliadores_pares,
          avaliadores_clientes: formData.avaliadores_clientes,
          avaliadores_subordinados: formData.avaliadores_subordinados
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Avaliação 360º criada com sucesso! ${totalAvaliadores + 1} avaliações geradas.`);

      setFormData({
        nome: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        funcionario_avaliado: '',
        avaliadores_gestor: [],
        avaliadores_pares: [],
        avaliadores_clientes: [],
        avaliadores_subordinados: []
      });
      
      carregarDadosIniciais();
    } catch (error) {
      console.error('Erro ao criar avaliação:', error);
      toast.error('Erro ao criar avaliação 360º');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalhes = (ciclo) => {
    setCicloDetalhes(ciclo);
    setModalDetalhesAberto(true);
  };

  const handleVerRespostas = async (ciclo) => {
    setLoadingRespostas(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback360/ciclo/${ciclo.id}/respostas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRespostasCiclo({
        ...ciclo,
        avaliacoes: response.data
      });
      setModalRespostasAberto(true);
    } catch (error) {
      console.error('Erro ao carregar respostas:', error);
      toast.error('Erro ao carregar respostas');
    } finally {
      setLoadingRespostas(false);
    }
  };

  const handleExportarExcel = () => {
    if (!respostasCiclo || !respostasCiclo.avaliacoes) {
      toast.error('Nenhum dado disponível para exportar');
      return;
    }

    try {
      const dadosExportacao = [];
      
      respostasCiclo.avaliacoes.forEach((avaliacao) => {
        if (avaliacao.respostas && avaliacao.respostas.length > 0) {
          avaliacao.respostas.forEach((resposta) => {
            dadosExportacao.push({
              'Avaliador': avaliacao.avaliador_nome || 'N/A',
              'Avaliado': avaliacao.avaliado_nome || 'N/A',
              'Tipo de Avaliador': avaliacao.tipo_avaliador || 'N/A',
              'Competência': resposta.competencia || 'N/A',
              'Pergunta': resposta.pergunta || 'N/A',
              'Nota (1-5)': resposta.resposta || 'N/A',
              'Data': avaliacao.respondido_em 
                ? new Date(avaliacao.respondido_em).toLocaleDateString('pt-BR')
                : 'N/A'
            });
          });
        }
      });

      const ws = XLSX.utils.json_to_sheet(dadosExportacao);
      const colWidths = [
        { wch: 25 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 60 },
        { wch: 12 },
        { wch: 12 }
      ];
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Respostas Feedback 360');

      const nomeArquivo = `Feedback_360_${respostasCiclo.avaliado_nome.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, nomeArquivo);
      
      toast.success('Arquivo Excel exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar arquivo Excel');
    }
  };

  const handleExcluirCiclo = async (cicloId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/feedback360/ciclos/${cicloId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Avaliação excluída com sucesso!');
      carregarDadosIniciais();
    } catch (error) {
      console.error('Erro ao excluir avaliação:', error);
      toast.error('Erro ao excluir avaliação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Target className="h-8 w-8 mr-3 text-purple-600" />
          Gerenciar Feedback 360º
        </h1>
        <p className="text-gray-600 mt-1">
          Crie e gerencie avaliações 360º completas
        </p>
      </div>

      <Tabs defaultValue="criar" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="criar" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Criar Avaliação 360º
          </TabsTrigger>
          <TabsTrigger value="pendentes" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Feedbacks Pendentes
          </TabsTrigger>
          <TabsTrigger value="concluidos" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Feedbacks Concluídos
          </TabsTrigger>
        </TabsList>

        {/* ABA 1: CRIAR AVALIAÇÃO */}
        <TabsContent value="criar">
          <Card>
            <CardHeader>
              <CardTitle>Nova Avaliação 360º</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Preencha os dados e selecione os avaliadores em uma única etapa
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Dados da Avaliação</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome da Avaliação *
                      </label>
                      <input
                        type="text"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="Ex: Avaliação 360º - João Silva"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrição
                      </label>
                      <input
                        type="text"
                        value={formData.descricao}
                        onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                        placeholder="Descrição opcional"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data de Início *
                      </label>
                      <input
                        type="date"
                        value={formData.data_inicio}
                        onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data de Fim *
                      </label>
                      <input
                        type="date"
                        value={formData.data_fim}
                        onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 border-t pt-6">
                  <h3 className="font-semibold text-gray-900">Funcionário a ser Avaliado *</h3>
                  <select
                    value={formData.funcionario_avaliado}
                    onChange={(e) => setFormData({ ...formData, funcionario_avaliado: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Selecione um funcionário</option>
                    {funcionarios.map(func => (
                      <option key={func.id} value={func.id}>
                        {func.nome || func.nome_completo || 'Sem nome'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-4 border-t pt-6">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-blue-600" />
                    Avaliador - Gestor (Líder Direto)
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Selecione o gestor/líder que irá avaliar o desempenho do funcionário
                  </p>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar gestor por nome..."
                      value={filtroGestor}
                      onChange={(e) => setFiltroGestor(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {funcionarios.filter(f => f.id !== formData.funcionario_avaliado && (f.nome || f.nome_completo || '').toLowerCase().includes(filtroGestor.toLowerCase())).map(func => (
                      <label key={func.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.avaliadores_gestor.includes(func.id)}
                          onChange={() => setFormData({
                            ...formData,
                            avaliadores_gestor: toggleAvaliador(formData.avaliadores_gestor, func.id)
                          })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{func.nome || func.nome_completo || 'Sem nome'}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-0.5">
                      {formData.avaliadores_gestor.length} selecionado{formData.avaliadores_gestor.length !== 1 ? 's' : ''}
                    </span>
                    {formData.avaliadores_gestor.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, avaliadores_gestor: [] })}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline flex items-center gap-1"
                      >
                        <XCircle className="h-3 w-3" />
                        Limpar
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4 border-t pt-6">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-green-600" />
                    Avaliadores - Pares (mesmo nível hierárquico)
                  </h3>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar par por nome..."
                      value={filtroPares}
                      onChange={(e) => setFiltroPares(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {funcionarios.filter(f => f.id !== formData.funcionario_avaliado && (f.nome || f.nome_completo || '').toLowerCase().includes(filtroPares.toLowerCase())).map(func => (
                      <label key={func.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.avaliadores_pares.includes(func.id)}
                          onChange={() => setFormData({
                            ...formData,
                            avaliadores_pares: toggleAvaliador(formData.avaliadores_pares, func.id)
                          })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{func.nome || func.nome_completo || 'Sem nome'}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-0.5">
                      {formData.avaliadores_pares.length} selecionado{formData.avaliadores_pares.length !== 1 ? 's' : ''}
                    </span>
                    {formData.avaliadores_pares.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, avaliadores_pares: [] })}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline flex items-center gap-1"
                      >
                        <XCircle className="h-3 w-3" />
                        Limpar
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4 border-t pt-6">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-purple-600" />
                    Avaliadores - Clientes Internos
                  </h3>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar cliente interno por nome..."
                      value={filtroClientes}
                      onChange={(e) => setFiltroClientes(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {funcionarios.filter(f => f.id !== formData.funcionario_avaliado && (f.nome || f.nome_completo || '').toLowerCase().includes(filtroClientes.toLowerCase())).map(func => (
                      <label key={func.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.avaliadores_clientes.includes(func.id)}
                          onChange={() => setFormData({
                            ...formData,
                            avaliadores_clientes: toggleAvaliador(formData.avaliadores_clientes, func.id)
                          })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{func.nome || func.nome_completo || 'Sem nome'}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-0.5">
                      {formData.avaliadores_clientes.length} selecionado{formData.avaliadores_clientes.length !== 1 ? 's' : ''}
                    </span>
                    {formData.avaliadores_clientes.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, avaliadores_clientes: [] })}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline flex items-center gap-1"
                      >
                        <XCircle className="h-3 w-3" />
                        Limpar
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4 border-t pt-6">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-orange-600" />
                    Avaliadores - Subordinados (equipe)
                  </h3>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Buscar subordinado por nome..."
                      value={filtroSubordinados}
                      onChange={(e) => setFiltroSubordinados(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {funcionarios.filter(f => f.id !== formData.funcionario_avaliado && (f.nome || f.nome_completo || '').toLowerCase().includes(filtroSubordinados.toLowerCase())).map(func => (
                      <label key={func.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={formData.avaliadores_subordinados.includes(func.id)}
                          onChange={() => setFormData({
                            ...formData,
                            avaliadores_subordinados: toggleAvaliador(formData.avaliadores_subordinados, func.id)
                          })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{func.nome || func.nome_completo || 'Sem nome'}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-0.5">
                      {formData.avaliadores_subordinados.length} selecionado{formData.avaliadores_subordinados.length !== 1 ? 's' : ''}
                    </span>
                    {formData.avaliadores_subordinados.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, avaliadores_subordinados: [] })}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline flex items-center gap-1"
                      >
                        <XCircle className="h-3 w-3" />
                        Limpar
                      </button>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Resumo da Avaliação</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Total de avaliações: {formData.avaliadores_gestor.length + formData.avaliadores_pares.length + formData.avaliadores_clientes.length + formData.avaliadores_subordinados.length + 1} (incluindo auto-avaliação)</li>
                    <li>• Gestor: {formData.avaliadores_gestor.length}</li>
                    <li>• Pares: {formData.avaliadores_pares.length}</li>
                    <li>• Clientes Internos: {formData.avaliadores_clientes.length}</li>
                    <li>• Subordinados: {formData.avaliadores_subordinados.length}</li>
                    <li>• Auto-avaliação: 1</li>
                  </ul>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={criarAvaliacaoCompleta}
                    disabled={loading}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {loading ? 'Criando...' : 'Criar Avaliação 360º'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA 2: FEEDBACKS PENDENTES */}
        <TabsContent value="pendentes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="h-5 w-5 mr-2 text-yellow-600" />
                Feedbacks Pendentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendentes.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhum feedback pendente</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendentes.map((ciclo) => (
                    <div key={ciclo.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{ciclo.avaliado_nome}</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {ciclo.total_avaliadores} avaliador(es)
                          </p>
                          {ciclo.avaliadores_nomes && ciclo.avaliadores_nomes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {ciclo.avaliadores_nomes.map((nome, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  <Users className="h-3 w-3 mr-1" />
                                  {nome}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                            {ciclo.respondidas}/{ciclo.total_avaliacoes} Respondidas
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(ciclo.criado_em).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full transition-all"
                          style={{ width: `${(ciclo.respondidas / ciclo.total_avaliacoes) * 100}%` }}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {ciclo.pendentes} pendente(s)
                          </span>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleVerDetalhes(ciclo)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Ver Detalhes
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleExcluirCiclo(ciclo.id)}
                              disabled={loading}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </Button>
                          </div>
                        </div>
                        
                        {ciclo.pendentes_detalhes && ciclo.pendentes_detalhes.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs font-semibold text-red-700 mb-1">⚠️ Aguardando resposta de:</p>
                            <div className="flex flex-wrap gap-1">
                              {ciclo.pendentes_detalhes.map((pendente, idx) => (
                                <Badge key={idx} variant="destructive" className="text-xs">
                                  {pendente.nome} ({pendente.tipo})
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA 3: FEEDBACKS CONCLUÍDOS */}
        <TabsContent value="concluidos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                Feedbacks Concluídos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {concluidos.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhum feedback concluído</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {concluidos.map((ciclo) => (
                    <div key={ciclo.id} className="border rounded-lg p-4 bg-green-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{ciclo.avaliado_nome}</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {ciclo.total_avaliadores} avaliador(es)
                          </p>
                          {ciclo.avaliadores_nomes && ciclo.avaliadores_nomes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {ciclo.avaliadores_nomes.map((nome, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  <Users className="h-3 w-3 mr-1" />
                                  {nome}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-600 text-white">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Concluído
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(ciclo.criado_em).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-green-700 font-medium">
                          {ciclo.total_avaliacoes} avaliações respondidas
                        </span>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleVerRespostas(ciclo)}
                            disabled={loadingRespostas}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            {loadingRespostas ? 'Carregando...' : 'Ver Respostas'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleExcluirCiclo(ciclo.id)}
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Detalhes */}
      {modalDetalhesAberto && cicloDetalhes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setModalDetalhesAberto(false)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Detalhes do Feedback 360º</h2>
              <button
                onClick={() => setModalDetalhesAberto(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Funcionário Avaliado</h3>
                <p className="text-gray-700">{cicloDetalhes.avaliado_nome}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Avaliadores ({cicloDetalhes.total_avaliadores})</h3>
                <div className="flex flex-wrap gap-2">
                  {cicloDetalhes.avaliadores_nomes && cicloDetalhes.avaliadores_nomes.map((nome, idx) => (
                    <Badge key={idx} variant="secondary">
                      <Users className="h-3 w-3 mr-1" />
                      {nome}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Estatísticas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Total de Avaliações</p>
                    <p className="text-2xl font-bold">{cicloDetalhes.total_avaliacoes}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Respondidas</p>
                    <p className="text-2xl font-bold text-green-600">{cicloDetalhes.respondidas}</p>
                  </div>
                  {cicloDetalhes.pendentes !== undefined && (
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600">Pendentes</p>
                      <p className="text-2xl font-bold text-yellow-600">{cicloDetalhes.pendentes}</p>
                    </div>
                  )}
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Criado em</p>
                    <p className="text-lg font-semibold">{new Date(cicloDetalhes.criado_em).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setModalDetalhesAberto(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Respostas Completo */}
      {modalRespostasAberto && respostasCiclo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setModalRespostasAberto(false)}>
          <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Tabulação Completa de Respostas</h2>
                <p className="text-gray-600 mt-1">Avaliado: {respostasCiclo.avaliado_nome}</p>
              </div>
              <button
                onClick={() => setModalRespostasAberto(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Avaliador</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Avaliado</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Tipo</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Pergunta</th>
                    <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Nota (1-5)</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {respostasCiclo.avaliacoes && respostasCiclo.avaliacoes.length > 0 ? (
                    respostasCiclo.avaliacoes.map((avaliacao, idx) => (
                      avaliacao.respostas && avaliacao.respostas.length > 0 ? (
                        avaliacao.respostas.map((resposta, rIdx) => (
                          <tr key={`${idx}-${rIdx}`} className={rIdx === 0 ? 'border-t-2 border-gray-400' : ''}>
                            {rIdx === 0 && (
                              <>
                                <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                  <div className="font-medium">{avaliacao.avaliador_nome || 'N/A'}</div>
                                </td>
                                <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                  <div className="font-medium">{avaliacao.avaliado_nome || 'N/A'}</div>
                                </td>
                                <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                  <Badge variant="secondary">
                                    {avaliacao.tipo_avaliador || 'N/A'}
                                  </Badge>
                                </td>
                              </>
                            )}
                            <td className="border border-gray-300 px-4 py-2">
                              <div className="text-sm">{resposta.pergunta}</div>
                              {resposta.competencia && (
                                <Badge variant="outline" className="mt-1 text-xs">
                                  {resposta.competencia}
                                </Badge>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              <span className={`font-bold text-lg ${
                                resposta.resposta >= 4 ? 'text-green-600' : 
                                resposta.resposta >= 3 ? 'text-yellow-600' : 
                                'text-red-600'
                              }`}>
                                {resposta.resposta || 'N/A'}
                              </span>
                            </td>
                            {rIdx === 0 && (
                              <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                <div className="text-sm">
                                  {avaliacao.respondido_em ? new Date(avaliacao.respondido_em).toLocaleDateString('pt-BR') : 'N/A'}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr key={idx}>
                          <td colSpan="6" className="border border-gray-300 px-4 py-2 text-center text-gray-500">
                            Sem respostas para esta avaliação
                          </td>
                        </tr>
                      )
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                        Nenhuma resposta encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {respostasCiclo.avaliacoes && respostasCiclo.avaliacoes.length > 0 && (
              <div className="mt-6 grid grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Total de Avaliações</p>
                  <p className="text-2xl font-bold">{respostasCiclo.avaliacoes.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Respondidas</p>
                  <p className="text-2xl font-bold text-green-600">
                    {respostasCiclo.avaliacoes.filter(a => a.status === 'respondida').length}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Total de Respostas</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {respostasCiclo.avaliacoes.reduce((sum, a) => sum + (a.respostas?.length || 0), 0)}
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Média Geral (Perguntas 1-20)</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {(() => {
                      const todasRespostas = respostasCiclo.avaliacoes.flatMap(a => a.respostas || []);
                      
                      // DEBUG: Ver o formato das respostas
                      console.log('🔍 DEBUG - Todas as respostas:', todasRespostas);
                      console.log('🔍 DEBUG - Tipos das respostas:', todasRespostas.map(r => ({ 
                        resposta: r.resposta, 
                        tipo: typeof r.resposta,
                        valor: r.resposta 
                      })));
                      
                      // Filtrar apenas respostas numéricas válidas (excluir pergunta 21 - texto livre)
                      const respostasNumericas = todasRespostas.filter(r => {
                        const resposta = r.resposta;
                        // Aceitar números entre 1 e 5 (escala Likert)
                        return typeof resposta === 'number' && resposta >= 1 && resposta <= 5 && !isNaN(resposta);
                      });
                      
                      console.log('🔍 DEBUG - Respostas numéricas filtradas:', respostasNumericas);
                      
                      const media = respostasNumericas.length > 0 
                        ? (respostasNumericas.reduce((sum, r) => sum + r.resposta, 0) / respostasNumericas.length).toFixed(1)
                        : '0.0';
                      
                      console.log('🔍 DEBUG - Média calculada:', media);
                      return media;
                    })()}
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-between">
              <Button 
                variant="outline"
                onClick={handleExportarExcel}
              >
                📊 Exportar para Excel
              </Button>
              <Button onClick={() => setModalRespostasAberto(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback360MainV2;
